import { useState } from "react";
import { TIER_ORDER, DAYS, TIME_SLOTS } from "../utils/tiers";

// A blank form for creating a single player by hand. Mirrors the fields the
// uploaded-file path accepts, so both routes produce the same schema once run
// through normalizeUploadedPlayer.
export default function AddPlayerModal({ players = [], onSubmit, onClose }) {
  const [draft, setDraft] = useState({
    name: "",
    email: "",
    householdId: "",
    classification: "Adult",
    tier: "No Group",
    age: "",
    gender: "",
    skillLevel: "",
    availability: [],
    preferredPartners: "",
    notes: "",
    privateTimes: "",
  });
  const [error, setError] = useState(null);
  const [emailError, setEmailError] = useState(null);

  // Who this player would join, shown as the id is typed. Adding a family
  // member is the usual reason to fill the field in, and a mistyped id fails
  // silently otherwise — it just creates a household of one.
  const householdKey = draft.householdId.trim();
  const householdMates = householdKey
    ? players.filter(p => String(p.householdId ?? "").trim() === householdKey)
    : [];

  function set(field, value) {
    setDraft(prev => ({ ...prev, [field]: value }));
  }

  function toggleAvailability(day, time) {
    const key = `${day}|${time}`;
    setDraft(prev => {
      const s = new Set(prev.availability);
      if (s.has(key)) s.delete(key);
      else s.add(key);
      return { ...prev, availability: [...s] };
    });
  }

  function handleSubmit() {
    if (!draft.name.trim()) {
      setError("Name is required.");
      return;
    }
    // Same shape of check the player modal makes on an edit: an address that
    // can't be sent to is worse than none at all, because the roster then
    // counts this member as reachable.
    const email = draft.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("That doesn't look like an email address.");
      return;
    }
    onSubmit({
      ...draft,
      name: draft.name.trim(),
      email,
      householdId: draft.householdId.trim(),
      age: draft.age === "" ? null : Number(draft.age),
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        style={{ maxHeight: "90vh" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-blue-600 text-white px-5 py-4 flex-shrink-0 flex items-center justify-between">
          <div className="text-lg font-bold">Add a player</div>
          <button onClick={onClose} className="text-2xl leading-none opacity-70 hover:opacity-100 transition-opacity">×</button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {/* Name */}
          <div>
            <SectionLabel>Name *</SectionLabel>
            <input
              type="text"
              value={draft.name}
              onChange={e => { set("name", e.target.value); if (error) setError(null); }}
              placeholder="Full name"
              autoFocus
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Contact. Left blank a player is unreachable: no schedule email,
              and nothing to look their schedule up by on the My Schedule
              page — unless a household id ties them to someone who has an
              address, which is how juniors are usually reached. */}
          <div>
            <SectionLabel>Email</SectionLabel>
            <input
              type="email"
              value={draft.email}
              onChange={e => { set("email", e.target.value); if (emailError) setEmailError(null); }}
              placeholder="name@example.com"
              autoComplete="off"
              className={`border rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 ${
                emailError ? "border-red-400 focus:ring-red-400" : "border-gray-300 focus:ring-blue-400"
              }`}
            />
            {emailError && <p className="text-xs text-red-600 mt-1">{emailError}</p>}
          </div>

          <div>
            <SectionLabel>Household ID</SectionLabel>
            <input
              type="text"
              value={draft.householdId}
              onChange={e => set("householdId", e.target.value)}
              placeholder="e.g. 032 — blank means no family link"
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {householdMates.length > 0 ? (
              <p className="text-xs text-gray-500 mt-1">
                Joins {householdMates.map(p => p.name).join(", ")}.
              </p>
            ) : householdKey ? (
              <p className="text-xs text-amber-600 mt-1">
                No one on the roster has this id yet — check it if you meant to link a family.
              </p>
            ) : (
              <p className="text-xs text-gray-400 mt-1">
                Members sharing an id see each other's schedules on the My Schedule page.
              </p>
            )}
          </div>

          {/* Age / Roster / Classification / Gender */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <SectionLabel>Age</SectionLabel>
              <input
                type="number"
                min="0"
                value={draft.age}
                onChange={e => set("age", e.target.value)}
                placeholder="—"
                className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <SectionLabel>Classification</SectionLabel>
              <select
                value={draft.classification}
                onChange={e => set("classification", e.target.value)}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
              >
                <option value="Adult">Adult</option>
                <option value="Junior">Junior</option>
              </select>
            </div>
            <div>
              <SectionLabel>Roster</SectionLabel>
              <select
                value={draft.tier}
                onChange={e => set("tier", e.target.value)}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
              >
                {TIER_ORDER.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <SectionLabel>Gender</SectionLabel>
              <select
                value={draft.gender}
                onChange={e => set("gender", e.target.value)}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
              >
                <option value="">— Not specified —</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
          </div>

          {/* Skill level */}
          <div>
            <SectionLabel>Skill Level</SectionLabel>
            <input
              type="text"
              value={draft.skillLevel}
              onChange={e => set("skillLevel", e.target.value)}
              placeholder="e.g. Intermediate (3.0–3.5)"
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Availability */}
          <div>
            <SectionLabel>Availability</SectionLabel>
            <div className="space-y-2">
              {DAYS.map(day => (
                <div key={day}>
                  <div className="text-xs font-semibold text-gray-500 mb-1">{day}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {TIME_SLOTS.map(time => {
                      const checked = draft.availability.includes(`${day}|${time}`);
                      return (
                        <label
                          key={time}
                          className={`flex items-center gap-1 text-xs px-2 py-1 rounded border cursor-pointer transition-colors ${
                            checked
                              ? "bg-blue-100 border-blue-300 text-blue-800"
                              : "bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleAvailability(day, time)}
                            className="sr-only"
                          />
                          {time}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Preferred partners */}
          <div>
            <SectionLabel>Preferred Partners</SectionLabel>
            <input
              type="text"
              value={draft.preferredPartners}
              onChange={e => set("preferredPartners", e.target.value)}
              placeholder="e.g. John Smith, Jane Doe"
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Notes */}
          <div>
            <SectionLabel>Notes</SectionLabel>
            <textarea
              value={draft.notes}
              onChange={e => set("notes", e.target.value)}
              placeholder="Any notes about this player…"
              rows={3}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
            />
          </div>

          {/* Private lesson times */}
          <div>
            <SectionLabel>Private Lesson Times</SectionLabel>
            <input
              type="text"
              value={draft.privateTimes}
              onChange={e => set("privateTimes", e.target.value)}
              placeholder="e.g. Tuesdays 5pm"
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</p>
          )}

          {/* Save / Cancel */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleSubmit}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
            >
              Add player
            </button>
            <button
              onClick={onClose}
              className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-600 text-sm font-medium py-2 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
      {children}
    </div>
  );
}
